import { useEffect, useState } from 'react';
import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material';
import type { ScoreSet } from 'shared';
import { DISPLAY, scoreHex, tokens } from '../theme';

const R = 40;
const CIRC = 2 * Math.PI * R;

/**
 * Một vòng tròn điểm — khối `\.ring` của demo.
 *
 * Vòng mờ phía sau là điểm CÓ THỂ đạt nếu áp dụng hết đề xuất; vòng đậm là điểm
 * hiện tại. Vẽ chồng hai vòng cùng bán kính để khoảng cách giữa hai mốc đọc được
 * ngay, không cần đối chiếu hai thanh rời.
 */
function Ring({
  value,
  potential,
  prev,
  caption,
}: {
  value: number;
  potential: number | null;
  prev: number | null;
  caption: string;
}) {
  // Vẽ từ 0 rồi chạy tới giá trị thật: mắt bắt được độ dài cung dễ hơn khi thấy
  // nó lớn dần. Người tắt hiệu ứng chuyển động sẽ thấy vòng hiện luôn.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, [value]);

  const offset = (v: number) => CIRC * (1 - (ready ? v : 0) / 100);
  const showPotential = potential !== null && potential > value;
  const diff = prev === null ? null : value - prev;

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        component="svg"
        width={100}
        height={100}
        viewBox="0 0 100 100"
        role="img"
        aria-label={`${caption} ${value} trên 100`}
        sx={{
          '& circle': { transition: 'stroke-dashoffset .9s ease .1s' },
          '@media (prefers-reduced-motion: reduce)': { '& circle': { transition: 'none' } },
        }}
      >
        <circle cx="50" cy="50" r={R} fill="none" stroke={tokens.line2} strokeWidth={9} />
        {showPotential && (
          <circle
            cx="50"
            cy="50"
            r={R}
            fill="none"
            stroke={tokens.mint}
            strokeOpacity={0.35}
            strokeWidth={9}
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={offset(potential)}
            transform="rotate(-90 50 50)"
          />
        )}
        <circle
          cx="50"
          cy="50"
          r={R}
          fill="none"
          stroke={scoreHex(value)}
          strokeWidth={9}
          strokeLinecap="round"
          strokeDasharray={CIRC}
          strokeDashoffset={offset(value)}
          transform="rotate(-90 50 50)"
        />
        <text
          x="50"
          y="58"
          textAnchor="middle"
          style={{ fontFamily: DISPLAY, fontSize: 25, fontWeight: 700, fill: tokens.ink }}
        >
          {value}
        </text>
      </Box>

      <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: 'text.secondary', mt: 0.6 }}>
        {caption}
      </Typography>
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 0.2, minHeight: 20 }}>
        {diff !== null && diff !== 0 && (
          <Typography
            sx={{ fontSize: 12.5, fontWeight: 600, color: diff > 0 ? tokens.okText : tokens.roseText }}
          >
            {diff > 0 ? `+${diff}` : diff} so với lần trước
          </Typography>
        )}
        {showPotential && diff === null && (
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.okText }}>
            có thể đạt {potential}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

/** Executive Summary — PRS §12: ba điểm số, xu hướng, và điểm dự kiến. */
export function ScorePanel({
  scores,
  potential,
  prev,
}: {
  scores: ScoreSet;
  potential: ScoreSet | null;
  prev: ScoreSet | null;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2.4, mb: 2.5 }}>
      <Tooltip title="Vòng mờ phía sau là điểm ước tính nếu áp dụng hết các đề xuất sửa được">
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: tokens.ink, width: 'fit-content' }}>
          Điểm số
        </Typography>
      </Tooltip>
      <Stack direction="row" spacing={4} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 2 }}>
        <Ring
          caption="SEO tổng"
          value={scores.seo}
          potential={potential?.seo ?? null}
          prev={prev?.seo ?? null}
        />
        <Ring
          caption="Kỹ thuật"
          value={scores.technical}
          potential={potential?.technical ?? null}
          prev={prev?.technical ?? null}
        />
        <Ring
          caption="Nội dung"
          value={scores.content}
          potential={potential?.content ?? null}
          prev={prev?.content ?? null}
        />
      </Stack>
    </Paper>
  );
}
